import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Text,
  Spinner,
  Dropdown,
  Option,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Dismiss24Regular, CheckmarkCircle24Regular } from "@fluentui/react-icons";
import { useWorkflowIntelligence } from "../contexts/WorkflowIntelligenceContext";
import { ExcelService } from "../services/excel.service";
import {
  previewBulkReviewMultiple,
  bulkReviewMultipleSheets,
  MultiSheetBulkReviewPreview,
  BulkReviewResult,
  getStatusItemTerminalStatus,
} from "../services/bulk-review.service";
import { ribbonService } from "../services/ribbon.service";
import { getStatusItemsBySpreadsheet } from "../services/status-items.service";
import {
  getStatusItemColor,
  getStatusItemTextColor,
} from "../utils/statusItemHelpers";
import { useTypographyStyles } from "../styles/typography.styles";

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "500px",
    minWidth: "400px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  pickerSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  sheetDropdown: {
    width: "100%",
  },
  previewSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  previewItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statusBadge: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  successMessage: {
    color: tokens.colorPaletteGreenForeground1,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  warningMessage: {
    color: tokens.colorPaletteYellowForeground1,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  spinnerContainer: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacingVerticalL,
  },
  discussionList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    margin: 0,
    paddingLeft: tokens.spacingHorizontalL,
    // Cap the list so a sheet with many open discussions doesn't push the
    // Complete button off-screen — the list scrolls instead.
    maxHeight: "200px",
    overflowY: "auto",
  },
});

interface BulkReviewDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSuccess?: () => void;
}

type DialogState = "loading" | "preview" | "executing" | "success" | "error";

const BulkReviewDialog: React.FC<BulkReviewDialogProps> = ({
  isOpen,
  onDismiss,
  onSuccess,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const { spreadsheetId, userRole } = useWorkflowIntelligence();

  const [state, setState] = useState<DialogState>("loading");
  // Sheets that have at least one (visible) status item — others are hidden
  // from the picker since signing them off would do nothing.
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [preview, setPreview] = useState<MultiSheetBulkReviewPreview | null>(null);
  const [result, setResult] = useState<BulkReviewResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [wasSuccessful, setWasSuccessful] = useState(false);

  // Initialise sheet list + default selection when the dialog opens.
  useEffect(() => {
    if (!isOpen) {
      setState("loading");
      setAvailableSheets([]);
      setSelectedSheets([]);
      setPreview(null);
      setResult(null);
      setErrorMessage(null);
      setWasSuccessful(false);
      return;
    }

    const init = async () => {
      setState("loading");
      setErrorMessage(null);

      try {
        if (!spreadsheetId) {
          setErrorMessage("No spreadsheet loaded.");
          setState("error");
          return;
        }

        const [activeName, worksheets, allItems] = await Promise.all([
          ExcelService.getActiveWorksheetName(),
          ExcelService.getAllWorksheetsInfo(),
          getStatusItemsBySpreadsheet(spreadsheetId),
        ]);

        const sheetIdsWithItems = new Set(
          allItems.filter((i) => !i.isHidden).map((i) => i.sheetId)
        );
        const sheetsWithItems = worksheets
          .filter((w) => sheetIdsWithItems.has(w.id))
          .map((w) => w.name);

        if (sheetsWithItems.length === 0) {
          setErrorMessage("No sheets with status items were found in this workbook.");
          setState("error");
          return;
        }

        setAvailableSheets(sheetsWithItems);

        // Default selection: the active sheet, if it has items. Otherwise nothing
        // — the user can pick from the dropdown.
        const defaultSelection = sheetsWithItems.includes(activeName)
          ? [activeName]
          : [];
        setSelectedSheets(defaultSelection);
      } catch (error) {
        setErrorMessage(`Failed to load sheets: ${error}`);
        setState("error");
      }
    };

    init();
  }, [isOpen, spreadsheetId]);

  // Recompute preview whenever the user toggles selected sheets. On the FIRST
  // load we flip to the loading spinner; subsequent toggles refresh in place
  // so the preview values just update without unmounting (avoids flicker).
  useEffect(() => {
    if (!isOpen || !spreadsheetId || availableSheets.length === 0) return undefined;
    if (state === "executing" || state === "success") return undefined;

    let cancelled = false;
    const load = async () => {
      if (!preview) setState("loading");
      const p = await previewBulkReviewMultiple(spreadsheetId, selectedSheets, userRole);
      if (cancelled) return;
      setPreview(p);
      if (!p.canPerformReview) {
        if (selectedSheets.length === 0) {
          setState("preview");
        } else {
          setErrorMessage(p.message ?? "Cannot perform bulk review.");
          setState("error");
        }
      } else {
        setState("preview");
      }
    };

    load();
    return () => { cancelled = true; };
  }, [isOpen, spreadsheetId, selectedSheets, userRole, availableSheets.length]);

  const handleExecute = async () => {
    if (!spreadsheetId || selectedSheets.length === 0) return;

    setState("executing");
    setErrorMessage(null);

    try {
      const bulkResult = await bulkReviewMultipleSheets(spreadsheetId, selectedSheets, userRole);
      setResult(bulkResult);

      if (bulkResult.errors.length > 0 && bulkResult.statusItemsUpdated === 0) {
        setErrorMessage(bulkResult.errors.join("\n"));
        setState("error");
      } else {
        setState("success");
        setWasSuccessful(true);
        // Broadcast so any page that's currently displaying status items can
        // refetch. Decoupled from the parent's `onSuccess` callback because the
        // dialog instance opened via the Excel ribbon is mounted inside
        // MainLayout — it has no direct handle to WorkflowStatus's state.
        window.dispatchEvent(new CustomEvent("statusItemsChanged"));
        // Re-colour every sheet tab to match its new lowest status, and
        // refresh the ribbon indicator for the active sheet. Without this,
        // any non-active sheet keeps its old tab colour until the user
        // clicks it (which is what triggers the per-sheet sync).
        await ribbonService.syncAllWorksheetTabColors();
        await ribbonService.updateRibbonStatusIndicator();
      }
    } catch (error) {
      setErrorMessage(`Failed to execute bulk review: ${error}`);
      setState("error");
    }
  };

  const handleClose = () => {
    if (wasSuccessful) {
      onSuccess?.();
    }
    onDismiss();
  };

  const statusItemTerminal = getStatusItemTerminalStatus(userRole);

  const totalToUpdate = preview?.totalStatusItemsToUpdate ?? 0;
  const aggregatedDiscussions = preview?.openDiscussionsAggregated ?? [];
  const hasStatusItemsToUpdate = totalToUpdate > 0;

  const sheetDropdownValue = useMemo(() => {
    if (selectedSheets.length === 0) return "Select sheets…";
    if (selectedSheets.length === 1) return selectedSheets[0];
    return `${selectedSheets.length} sheets selected`;
  }, [selectedSheets]);

  return (
    <Dialog open={isOpen} onOpenChange={(_e, data) => !data.open && onDismiss()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={onDismiss}
              />
            }
          >
            Sign Off Sheets
          </DialogTitle>

          <DialogContent className={styles.content}>
            {/* Sheet picker — always visible while in preview/loading/executing
                so the user can change selection without dismissing. */}
            {(state === "preview" || state === "loading" || state === "executing") &&
              availableSheets.length > 0 && (
                <div className={styles.pickerSection}>
                  <Text className={typography.formLabel}>Sheets to sign off</Text>
                  <Dropdown
                    className={styles.sheetDropdown}
                    multiselect
                    selectedOptions={selectedSheets}
                    value={sheetDropdownValue}
                    onOptionSelect={(_e, data) => setSelectedSheets(data.selectedOptions)}
                    disabled={state === "executing"}
                  >
                    {availableSheets.map((name) => (
                      <Option key={name} value={name}>
                        {name}
                      </Option>
                    ))}
                  </Dropdown>
                </div>
              )}

            {state === "loading" && (
              <div className={styles.spinnerContainer}>
                <Spinner size="medium" label="Loading preview..." />
              </div>
            )}

            {state === "executing" && (
              <div className={styles.spinnerContainer}>
                <Spinner size="medium" label="Updating items..." />
              </div>
            )}

            {state === "error" && errorMessage && (
              <div className={styles.errorMessage}>
                <Text weight="semibold">Error</Text>
                <br />
                <Text>{errorMessage}</Text>
              </div>
            )}

            {state === "preview" && preview && selectedSheets.length === 0 && (
              <div className={styles.warningMessage}>
                <Text>Select at least one sheet from the dropdown above.</Text>
              </div>
            )}

            {state === "preview" && preview && selectedSheets.length > 0 && (
              <>
                <Text>
                  This will mark all status items on the selected sheets as
                  complete based on your role permissions.
                </Text>

                <div className={styles.previewSection}>
                  <div className={styles.previewItem}>
                    <Text>Sheets selected:</Text>
                    <Text weight="semibold">{selectedSheets.length}</Text>
                  </div>
                  <div className={styles.previewItem}>
                    <Text>Status items to update:</Text>
                    <Text weight="semibold">{totalToUpdate}</Text>
                  </div>
                  <div className={styles.previewItem}>
                    <Text>Related discussions:</Text>
                    <Text weight="semibold">{aggregatedDiscussions.length}</Text>
                  </div>
                  {statusItemTerminal && (
                    <div className={styles.previewItem}>
                      <Text>Status items will be set to:</Text>
                      <span
                        className={styles.statusBadge}
                        style={{
                          backgroundColor: getStatusItemColor(statusItemTerminal),
                          color: getStatusItemTextColor(statusItemTerminal),
                        }}
                      >
                        {statusItemTerminal}
                      </span>
                    </div>
                  )}
                </div>

                {aggregatedDiscussions.length > 0 && (
                  <div className={styles.previewSection}>
                    <Text weight="semibold">
                      Open discussions on these sheets ({aggregatedDiscussions.length})
                    </Text>
                    <Text className={typography.caption}>
                      These discussions remain open after sign-off. Review and close
                      them individually from the Discussions page when appropriate.
                    </Text>
                    <ul className={styles.discussionList}>
                      {aggregatedDiscussions.map((d) => (
                        <li key={d.id}>
                          <Text>{d.title}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!hasStatusItemsToUpdate && (
                  <div className={styles.warningMessage}>
                    <Text>
                      No status items need to be updated on the selected sheets. All
                      items are already at or past your maximum review level.
                    </Text>
                  </div>
                )}
              </>
            )}

            {state === "success" && result && (
              <div className={styles.successMessage}>
                <Text weight="semibold">Successfully completed!</Text>
                <br />
                <Text>
                  Updated {result.statusItemsUpdated} status item(s) across {selectedSheets.length} sheet(s).
                </Text>
                {result.statusItemsFailed > 0 && (
                  <>
                    <br />
                    <Text className={typography.bodySmall}>
                      {result.statusItemsFailed} item(s) failed to update.
                    </Text>
                  </>
                )}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            {state === "preview" && (
              <>
                <Button appearance="secondary" onClick={onDismiss}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  icon={<CheckmarkCircle24Regular />}
                  onClick={handleExecute}
                  disabled={!hasStatusItemsToUpdate || selectedSheets.length === 0}
                >
                  Complete
                </Button>
              </>
            )}

            {(state === "success" || state === "error") && (
              <Button appearance="primary" onClick={handleClose}>
                Close
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default BulkReviewDialog;
